/**
 * 不可变更新工具
 * 用于安全地更新嵌套 JSON 结构，保证原对象不被修改
 */

import type { DiffEntry, SchemaDiff } from '../core/types';

// ============ 路径操作 ============

/**
 * 根据路径获取嵌套对象的值
 * @param obj - 目标对象
 * @param path - 属性路径数组
 * @returns 路径对应的值，如不存在返回 undefined
 */
export function getByPath<T = unknown>(
  obj: unknown,
  path: string[]
): T | undefined {
  return path.reduce<unknown>((current, key) => {
    if (current == null) return undefined;
    if (Array.isArray(current)) {
      const index = parseInt(key, 10);
      return Number.isNaN(index) ? undefined : current[index];
    }
    if (typeof current === 'object') {
      return (current as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj) as T | undefined;
}

/**
 * 检查路径是否存在
 */
export function hasPath(obj: unknown, path: string[]): boolean {
  return getByPath(obj, path) !== undefined;
}

// ============ 不可变更新 ============

/**
 * 不可变地设置嵌套对象的值
 * @param obj - 原始对象
 * @param path - 属性路径数组
 * @param value - 新值
 * @returns 新对象（原对象不变）
 */
export function immutableSet<T extends object>(
  obj: T,
  path: string[],
  value: unknown
): T {
  if (path.length === 0) {
    return value as T;
  }

  const [head, ...tail] = path;

  // 处理数组
  if (Array.isArray(obj)) {
    const index = parseInt(head, 10);
    if (Number.isNaN(index)) {
      throw new Error(`Invalid array index: ${head}`);
    }

    const newArray = [...obj];
    newArray[index] =
      tail.length === 0
        ? value
        : immutableSet(
            (obj[index] ?? {}) as object,
            tail,
            value
          );
    return newArray as unknown as T;
  }

  // 处理对象
  const current = obj as Record<string, unknown>;
  return {
    ...current,
    [head]:
      tail.length === 0
        ? value
        : immutableSet(
            (current[head] ?? {}) as object,
            tail,
            value
          ),
  } as T;
}

/**
 * 不可变地删除嵌套对象的属性
 * @param obj - 原始对象
 * @param path - 属性路径数组
 * @returns 新对象（原对象不变）
 */
export function immutableDelete<T extends object>(
  obj: T,
  path: string[]
): T {
  if (path.length === 0) {
    return obj;
  }

  if (path.length === 1) {
    if (Array.isArray(obj)) {
      const index = parseInt(path[0], 10);
      const newArray = [...obj];
      newArray.splice(index, 1);
      return newArray as unknown as T;
    }

    const { [path[0]]: _, ...rest } = obj as Record<string, unknown>;
    return rest as T;
  }

  const [head, ...tail] = path;
  const current = obj as Record<string, unknown>;
  const child = current[head];

  if (child == null || typeof child !== 'object') {
    return obj;
  }

  return {
    ...current,
    [head]: immutableDelete(child as object, tail),
  } as T;
}

/**
 * 批量不可变更新
 * @param obj - 原始对象
 * @param updates - 更新数组 [{path, value}]
 * @returns 新对象
 */
export function immutableBatchUpdate<T extends object>(
  obj: T,
  updates: Array<{ path: string[]; value: unknown }>
): T {
  return updates.reduce(
    (current, { path, value }) => immutableSet(current, path, value),
    obj
  );
}

// ============ Diff 计算 ============

/**
 * 比较两个对象，返回差异
 * @param before - 修改前的对象
 * @param after - 修改后的对象
 * @returns 差异数组
 */
export function computeDiff(
  before: unknown,
  after: unknown
): SchemaDiff {
  const changes: DiffEntry[] = [];

  function compare(pathPrefix: string[], a: unknown, b: unknown): void {
    // 相同值，无变化
    if (a === b) return;

    // 两个都是对象（非数组）
    if (
      a !== null &&
      b !== null &&
      typeof a === 'object' &&
      typeof b === 'object' &&
      !Array.isArray(a) &&
      !Array.isArray(b)
    ) {
      const aObj = a as Record<string, unknown>;
      const bObj = b as Record<string, unknown>;
      const allKeys = Array.from(new Set([...Object.keys(aObj), ...Object.keys(bObj)]));

      for (const key of allKeys) {
        compare([...pathPrefix, key], aObj[key], bObj[key]);
      }
      return;
    }

    // 两个都是数组
    if (Array.isArray(a) && Array.isArray(b)) {
      const maxLen = Math.max(a.length, b.length);
      for (let i = 0; i < maxLen; i++) {
        compare([...pathPrefix, String(i)], a[i], b[i]);
      }
      return;
    }

    // 基础类型或类型不同，记录变化
    changes.push({
      path: pathPrefix.join('.'),
      oldValue: a,
      newValue: b,
    });
  }

  compare([], before, after);
  return changes;
}

/**
 * 格式化 Diff 为可读字符串
 */
export function formatDiff(diff: SchemaDiff): string {
  if (diff.length === 0) {
    return 'No changes';
  }

  return diff
    .map((entry) => {
      const oldStr = JSON.stringify(entry.oldValue);
      const newStr = JSON.stringify(entry.newValue);
      return `${entry.path}:\n  - ${oldStr}\n  + ${newStr}`;
    })
    .join('\n\n');
}

// ============ 深拷贝 ============

/**
 * 深拷贝对象
 */
export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(deepClone) as unknown as T;
  }

  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    result[key] = deepClone((obj as Record<string, unknown>)[key]);
  }
  return result as T;
}

// ============ 路径解析 ============

/**
 * 将点分隔的路径字符串转换为数组
 * @example "numberingSchemas.AbstractNum_5.levels.0.indent.left" => ["numberingSchemas", "AbstractNum_5", "levels", "0", "indent", "left"]
 */
export function parsePath(pathString: string): string[] {
  return pathString.split('.');
}

/**
 * 将路径数组转换为点分隔的字符串
 */
export function stringifyPath(path: string[]): string {
  return path.join('.');
}

// ============ 数组操作 ============

/**
 * 不可变地在数组指定位置插入元素
 */
export function immutableArrayInsert<T>(
  arr: T[],
  index: number,
  item: T
): T[] {
  const newArr = [...arr];
  newArr.splice(index, 0, item);
  return newArr;
}

/**
 * 不可变地移动数组元素
 */
export function immutableArrayMove<T>(
  arr: T[],
  fromIndex: number,
  toIndex: number
): T[] {
  const newArr = [...arr];
  const [item] = newArr.splice(fromIndex, 1);
  newArr.splice(toIndex, 0, item);
  return newArr;
}
